// Port of ios/Sources/ViewModels/SearchQuery.swift.
//
// The webapp's search-box grammar (shared/API.md §23): bare words, "quoted phrases",
// `-exclusions` and the `from:` / `in:` / `before:` / `after:` / `on:` modifiers. A decision-
// for-decision port of Android's `SearchQuery`, tests included.
//
// The SERVER is still handed the query verbatim. Mattermost's own parser is the authority on
// what these mean, and re-serialising a parse of it could only ever lose — this parse exists
// for the LOCAL pass, which runs first. The store's FTS index knows only message text: it has
// no idea who wrote a post or when, so a cached-first result list has to apply the modifiers
// itself or it would show rows the server's answer is about to take away.
import { addingDays, epochForCivil } from './zonedTime';

export interface Parsed {
  words: string[];
  phrases: string[];
  excluded: string[];
  from: string[];
  inChannels: string[];
  before: string | null;
  after: string | null;
  on: string | null;
}

export function emptyParsed(): Parsed {
  return { words: [], phrases: [], excluded: [], from: [], inChannels: [], before: null, after: null, on: null };
}

export function hasText(parsed: Parsed): boolean {
  return parsed.words.length > 0 || parsed.phrases.length > 0;
}

export function hasModifiers(parsed: Parsed): boolean {
  return (
    parsed.from.length > 0 ||
    parsed.inChannels.length > 0 ||
    parsed.before != null ||
    parsed.after != null ||
    parsed.on != null
  );
}

/**
 * Nothing worth asking anyone. Deliberately NOT simply `!hasText`: "everything Ana
 * wrote yesterday" is a real desktop search with no search terms in it at all.
 */
export function isEmpty(parsed: Parsed): boolean {
  return !hasText(parsed) && !hasModifiers(parsed) && parsed.excluded.length === 0;
}

// MARK: - Parsing

export const keyFrom = 'from';
export const keyIn = 'in';
export const keyBefore = 'before';
export const keyAfter = 'after';
export const keyOn = 'on';

/** The modifier keys, in the order a search screen would offer them as chips. */
export const modifierKeys: readonly string[] = [keyFrom, keyIn, keyBefore, keyAfter, keyOn];

const WHITESPACE = /^\s$/u;

export function parse(raw: string): Parsed {
  const parsed = emptyParsed();
  for (const token of tokenize(raw)) {
    const text = token.text;
    // A token that OPENED with a quote is a phrase whatever it contains, so `"from:ana"`
    // searches for that text instead of quietly becoming a filter.
    if (token.startedQuoted) {
      if (trimSpaces(text) !== '') {
        parsed.phrases.push(fold(text));
      }
      continue;
    }

    const colon = text.indexOf(':');
    if (colon > 0) {
      const key = fold(text.slice(0, colon));
      const value = text.slice(colon + 1);
      if (modifierKeys.includes(key)) {
        // A recognised modifier with NO value yet is a keystroke on the way to one,
        // and is dropped rather than kept as a term — otherwise the instant someone
        // types the colon in "from:", the box searches for the literal word "from:".
        if (value !== '') {
          switch (key) {
            case keyFrom:
              parsed.from.push(fold(stripPrefix(value, '@')));
              break;
            case keyIn:
              // Both sigils a channel is written with: `~` for a channel, `@` for
              // the person a DM is with.
              parsed.inChannels.push(fold(stripPrefix(stripPrefix(value, '~'), '@')));
              break;
            case keyBefore:
              parsed.before = value;
              break;
            case keyAfter:
              parsed.after = value;
              break;
            default:
              parsed.on = value;
          }
        }
        continue;
      }
    }

    if ([...text].length > 1 && text.startsWith('-')) {
      parsed.excluded.push(fold(text.slice(1)));
      continue;
    }

    parsed.words.push(fold(text));
  }
  return parsed;
}

/** Swift's `trimmingCharacters(in: .whitespaces)`: spaces and tabs, not newlines. */
function trimSpaces(text: string): string {
  return text.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');
}

function stripPrefix(text: string, prefix: string): string {
  return text.startsWith(prefix) ? text.slice(prefix.length) : text;
}

/**
 * The query as the SERVER should receive it, with `in:@person` rewritten to the real channel
 * name of the conversation with that person.
 *
 * The box deliberately shows `in:@carlos.sc`, because a direct conversation's actual name is
 * `<userid>__<userid>` and nobody recognises their own DM by that. But `in:` is a filter on
 * the channel's NAME, so the pretty form is only certain to work locally — this client has
 * been handing it to the server verbatim and betting that Mattermost resolves the `@` form
 * too. A search that returns nothing looks identical whether the bet lost or the words
 * genuinely appear nowhere, which is how "the search doesn't work at all" (user,
 * 2026-09-08) can be true without a single error anywhere.
 *
 * So the bet is retired: what the reader types stays what the reader sees, and what goes on
 * the wire is the name the server indexes by. `resolve` returns null for a name it cannot map
 * — an unknown person, a channel this device has never synced — and that token is then left
 * exactly as typed, because a filter the client cannot translate is still one the server
 * might understand.
 */
export function serverTerms(raw: string, resolve: (name: string) => string | null | undefined): string {
  return raw
    .split(' ')
    .map((token) => {
      const colon = token.indexOf(':');
      if (colon <= 0) return token;
      if (fold(token.slice(0, colon)) !== keyIn) return token;
      const value = token.slice(colon + 1);
      if (!value.startsWith('@')) return token;
      const resolved = resolve(fold(stripPrefix(value, '@')));
      if (resolved == null) return token;
      return `${keyIn}:${resolved}`;
    })
    .join(' ');
}

/**
 * The query with a trailing `*` on the word still being typed — what makes the server answer
 * the same question the local index already answers.
 *
 * WHY THIS EXISTS. The local pass has always prefix-matched its last word (`matchExpression`),
 * and the server was handed the query verbatim. So "linke" found the cached message containing
 * "linkedin" at 120 ms, the server found nothing at 550 ms, and its empty answer replaced the
 * result the reader was already looking at — reported as "search shows results then it hides
 * them" (user, with a screenshot, 2026-09-13). The two passes disagreeing about what a word is
 * was the whole defect; this is the half that makes them agree.
 *
 * MEASURED against the live server (Mattermost 11.10.0, 2026-09-13) rather than assumed:
 *
 *  - `licenc` → **0 hits**, `licenc*` → 100. `reuni` → 0, `reuni*` → 100. The server does no
 *    prefix matching at all unless it is asked, which is the defect in one line.
 *  - `reunion` → 57 and `reunion*` → 57. A wildcard on a word that is ALREADY complete changes
 *    nothing, which is what makes this safe to apply unconditionally: it can only ever add
 *    prefix matches, never take a result away.
 *  - `"licencia de linke"*` → **0**. A wildcard after a quoted phrase does not widen it, it
 *    BREAKS it. Phrases are therefore left alone.
 *  - position does not matter (`reuni* proyecto` and `proyecto reuni*` both → 74) and a doubled
 *    `reuni**` is harmless — so the rule needs no care beyond picking the right token.
 *
 * WHAT IS DELIBERATELY LEFT ALONE: phrases (measured above); modifiers (`from:an*` is not a
 * person); exclusions (`-foo`) — everything else here can only ADD results, and widening an
 * exclusion is the one form that would REMOVE them; and a token already ending in `*`, which
 * the reader asked for themselves.
 *
 * Kotlin twin: `SearchQuery.withPrefixWildcard`.
 */
export function withPrefixWildcard(raw: string): string {
  const trimmedEnd = raw.replace(/ +$/, '');
  // A trailing space means the reader finished that word and is about to type the next one.
  // Widening the finished word then would change the results of a query they have moved on
  // from, and the character they type next re-runs all of this anyway.
  if (trimmedEnd.length !== raw.length || trimmedEnd === '') return raw;

  const pieces = trimmedEnd.split(' ');
  const last = pieces[pieces.length - 1];
  if (last == null || last === '') return raw;
  if (last.endsWith('*') || last.startsWith('-') || last.includes('"')) return raw;
  // Inside an unterminated phrase (`"licencia de linke`) the last token is part of the phrase,
  // not a word of its own — a quote count that is odd BEFORE the token is what says so.
  const before = pieces.slice(0, -1).join(' ');
  if ((before.match(/"/g)?.length ?? 0) % 2 !== 0) return raw;

  const colon = last.indexOf(':');
  if (colon > 0 && modifierKeys.includes(fold(last.slice(0, colon)))) {
    return raw;
  }
  // Nothing indexable in it (an emoji, punctuation on its own): the server would read the `*`
  // as the entire term.
  if (sanitize(last) == null) return raw;
  return raw + '*';
}

/**
 * `startedQuoted`: whether the token's FIRST character was the quote — the only thing that
 * separates a phrase (`"my channel"`) from a modifier whose value is quoted
 * (`in:"my channel"`) once the quote characters themselves are gone.
 */
interface Token {
  text: string;
  startedQuoted: boolean;
}

function tokenize(raw: string): Token[] {
  const tokens: Token[] = [];
  let current = '';
  let startedQuoted = false;
  let inQuotes = false;
  for (const character of raw) {
    if (character === '"') {
      if (current === '' && !inQuotes) startedQuoted = true;
      inQuotes = !inQuotes;
    } else if (WHITESPACE.test(character) && !inQuotes) {
      if (current !== '') tokens.push({ text: current, startedQuoted });
      current = '';
      startedQuoted = false;
    } else {
      current += character;
    }
  }
  // An unterminated quote still yields its token: the box is read on every keystroke, and
  // half of a typed phrase is the normal state of it.
  if (current !== '') tokens.push({ text: current, startedQuoted });
  return tokens;
}

// MARK: - The local FTS pass

/**
 * The FTS `MATCH` expression, or null when there is no text to match on. Every token is
 * stripped to letters, digits and underscores first — the FTS query language reads `*`,
 * `-`, `:`, `(`, `^` and `"` as syntax and `AND`/`OR`/`NOT`/`NEAR` as operators, so one of
 * them arriving straight from a search box throws mid-keystroke. Folding disarms the
 * operator words too. The LAST word carries a trailing `*` so "reuni" matches while typed.
 * Exclusions never reach the expression (a query made only of them is a syntax error in
 * that language) — `matches` applies them over the far smaller set this returns.
 */
export function matchExpression(parsed: Parsed): string | null {
  const terms: string[] = [];
  for (const phrase of parsed.phrases) {
    const inner = phrase
      .split(' ')
      .filter((p) => p !== '')
      .map(sanitize)
      .filter((s): s is string => s != null);
    if (inner.length > 0) terms.push('"' + inner.join(' ') + '"');
  }
  const words = parsed.words.map(sanitize).filter((s): s is string => s != null);
  words.forEach((word, index) => {
    terms.push(index === words.length - 1 ? word + '*' : word);
  });
  return terms.length === 0 ? null : terms.join(' ');
}

/**
 * Letters, digits and underscores only (Swift's `Character.isLetter || isNumber || "_"`). A
 * combining mark is kept when it follows a kept character, because Swift judges the whole
 * grapheme — a decomposed "é" is one letter there, not a letter and a dropped accent.
 */
export function sanitize(token: string): string | null {
  const kept = token.match(/[\p{L}\p{N}_]\p{M}*/gu);
  const folded = fold(kept ? kept.join('') : '');
  return folded === '' ? null : folded;
}

/**
 * What `matches` needs to know about one candidate. Deliberately not a `PostRecord` plus
 * two store rows: the rule is then testable without a database. `channelSlug` is the
 * channel's `name` (its URL segment) — that is what `in:` speaks on the desktop;
 * `channelAliases` are the other names it answers to (a DM partner's username, whose slug
 * is a pair of user ids nobody would type).
 */
export interface Candidate {
  message: string;
  authorUsername: string;
  channelSlug: string;
  createAt: number;
  channelAliases?: Iterable<string>;
}

/**
 * Whether `candidate` survives `parsed`'s modifiers. Text matching is NOT re-checked here —
 * the FTS index already did it. A malformed date narrows nothing rather than everything:
 * `before:2026-08` is a date being typed, and emptying the results at that keystroke would
 * look like the search broke.
 */
export function matches(parsed: Parsed, candidate: Candidate, timeZone?: string): boolean {
  if (parsed.excluded.length > 0) {
    const message = fold(candidate.message);
    if (parsed.excluded.some((e) => message.includes(e))) return false;
  }
  if (parsed.from.length > 0 && !parsed.from.includes(fold(candidate.authorUsername))) return false;
  if (parsed.inChannels.length > 0) {
    const names = new Set<string>();
    for (const alias of candidate.channelAliases ?? []) names.add(fold(alias));
    names.add(fold(candidate.channelSlug));
    if (!parsed.inChannels.some((c) => names.has(c))) return false;
  }

  if (parsed.on != null) {
    const start = dayStart(parsed.on, timeZone);
    const end = dayAfter(parsed.on, timeZone);
    if (start != null && end != null && (candidate.createAt < start || candidate.createAt >= end)) return false;
  }
  if (parsed.before != null) {
    const start = dayStart(parsed.before, timeZone);
    if (start != null && candidate.createAt >= start) return false;
  }
  if (parsed.after != null) {
    const end = dayAfter(parsed.after, timeZone);
    // `after:` excludes the named day itself, matching the desktop: "after Monday" is
    // Tuesday onwards, not "some time on Monday".
    if (end != null && candidate.createAt < end) return false;
  }
  return true;
}

// MARK: - Dates

function dayStart(date: string, timeZone: string | undefined): number | null {
  return dateFor(date, timeZone);
}

/**
 * Midnight starting the NEXT day, via calendar arithmetic rather than +86,400,000 — the
 * day a clock change falls on is 23 or 25 hours long.
 */
function dayAfter(date: string, timeZone: string | undefined): number | null {
  const day = dateFor(date, timeZone);
  if (day == null) return null;
  return addingDays(day, 1, timeZone);
}

function dateFor(date: string, timeZone: string | undefined): number | null {
  const parts = date.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(parseSwiftInt);
  if (year == null || month == null || day == null) return null;
  if (year < 1970 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return epochForCivil(year, month, day, 0, 0, 0, timeZone);
}

/** Swift's `Int(String)`: optional sign, ASCII digits, nothing else. */
function parseSwiftInt(text: string): number | null {
  return /^[+-]?[0-9]+$/.test(text) ? Number(text) : null;
}

/**
 * Case folding that CANNOT change a string's length, character by character — a whole-
 * string lowercase can (`İ` becomes two characters), and any highlight offset would be
 * measured against a folded copy of the string a row draws.
 *
 * On the web "length" is UTF-16 length (the unit highlight offsets are in), so a code point is
 * lowered only when its lowercase form has the same UTF-16 length.
 */
export function fold(text: string): string {
  let folded = '';
  for (const character of text) {
    const lowered = character.toLowerCase();
    folded += lowered.length === character.length ? lowered : character;
  }
  return folded;
}
